import { alpha, RadioGroup, Stack, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { CircleAlert, Hourglass, InfoIcon } from "lucide-react";
import {
  useEffect,
  useState } from "react";
import { useTranslation } from "react-i18next";
import type { IEditions } from "types";
import { EditionsContainer } from "./Editions.styles";
import { canSelfEnroll, isEnrolledLoStatus, oneEditionIsEnrolled } from "./utils";
import { ButtonDigited } from "../../../components/Button/Button.variants";
import { TitleAndSubtitle } from "../../../components/Commons/TitleAndSubtitle.component";
import { useDescendantClassrooms } from "../../../components/DetailsEsploraRestyle/DescendantsClassrooms.context";
import { EditionInProgressWarningModal } from "../../../components/EditionInProgressWarningModal";
import { InfoBanner } from "../../../components/InfoBanner/InfoBanner.component";
import { EditionCard } from "../../../components/ModalEditionChange/EditionCard.component";
import { UnsubscribeEditionConfirmModal } from "../../../components/UnsubscribeEditionConfirmModal";
import { ENROLL_TYPE, STATUS_LO } from "../../../consts";
import { SkeletonCard } from "../../ModalEditionChange/SkeletonCard";

export const Editions = ({
  title,
  description,
  descriptionEnrolled,
  learningObject,
  isVirtual,
  isLoading,
  noPadding=true,
  editionsQueryData,
  hidePendingApprovalBanner=false
}: IEditions) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, i18n } = useTranslation();
  const [selectedEditionId, setSelectedEditionId] = useState<number | null>(null);
  const [expandedEditionId, setExpandedEditionId] = useState<number | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState<boolean>(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState<boolean>(false);
  const [showChangeEditionWarning, setShowChangeEditionWarning] = useState<boolean>(false);

  const { selfEnrollment } = learningObject;

  const selfEnrollAllowed = canSelfEnroll(selfEnrollment, learningObject.enrollmentEnrollType)
    && learningObject.isEnable !== false;

  // Auto-iscrizione con approvazione: se il padre (percorso/corso) o, per le attività standalone,
  // l'oggetto stesso ha enrollType "REQUESTED_AUTO_ENROLL", l'iscrizione a un'edizione passa
  // da una richiesta di approvazione del responsabile, a meno che l'approvazione non sia già
  // stata concessa (padre con status "E"/"P"/"C": vedi isEnrollmentApprovalPending). In quel caso
  // lo stato del padre vince e la label torna "enroll_edition".
  const { enrollType, approvalGranted } = useDescendantClassrooms();
  const enrollmentNeedsApproval = enrollType === ENROLL_TYPE.REQUESTED_AUTO_ENROLL && !approvalGranted;

  const {
    editions,
    handleEditionChange,
    enrollInEdition,
    isEnrolling,
    isChangingEdition,
    isPollingEditionChange,
    editionsIsFetching,
    refetchEditions,
    invalidateDescendantsClassrooms,
    invalidateLearningObject
  } = editionsQueryData ?? {};

  const enrolled = oneEditionIsEnrolled(editions);
  const enrolledEdition = editions?.find((edition) => edition.status === "E" || edition.status === "P");
  const isUserEdition = isEnrolledLoStatus;
  const hasUserEdition = editions?.some((edition) => isUserEdition(edition.status)) ?? false;
  const hasCompletedEdition = (editions ?? []).some((edition) => edition.status === STATUS_LO.C)
    || learningObject.status === STATUS_LO.C;
  dayjs.locale(i18n.language);


  const isEnrolledEditionInProgress = enrolledEdition?.status === STATUS_LO.P;

  // possiamo derivare lo stato "in attesa" dal BE, senza bridge locale in sessionStorage.
  const waitingApprovalEdition = (editions ?? []).find((edition) => edition.status === STATUS_LO.W);
  const hasWaitingApprovalEdition = !!waitingApprovalEdition;
  const hasPendingRequest = enrollmentNeedsApproval && hasWaitingApprovalEdition;

  // Prima iscrizione all'aula (nessuna edizione ancora "E"/"P"): serve la POST base prima della
  // PATCH sull'edizione. Un cambio edizione, invece, presuppone
  // un'iscrizione già esistente: solo PATCH.
  const submitEnrollment = (editionId: number) => {
    const submit = enrolled ? handleEditionChange : enrollInEdition;
    if (!submit) return;
    submit(editionId);
  };

  const handleEnroll = () => {
    if (selectedEditionId === null || (!handleEditionChange && !enrollInEdition)) return;

    if (isEnrolledEditionInProgress) {
      setShowChangeEditionWarning(true);
      return;
    }

    submitEnrollment(selectedEditionId);
  };

  const confirmChangeEdition = () => {
    setShowChangeEditionWarning(false);
    if (selectedEditionId !== null && (handleEditionChange || enrollInEdition)) {
      submitEnrollment(selectedEditionId);
    }
  };

  const handleEnterEditMode = () => {
    setSelectedEditionId(enrolledEdition?.id ?? null);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setSelectedEditionId(enrolledEdition?.id ?? null);
    setExpandedEditionId(null);
  };

  const toggleAccordion = (editionId: number) => {
    setExpandedEditionId(expandedEditionId === editionId ? null : editionId);
  };

  // Navigazione da tastier:
  // il focus sulla card apre l'accordion e Invio equivale al click sul radio.
  const expandEdition = (editionId: number) => {
    setExpandedEditionId(editionId);
  };

  const selectEdition = (editionId: number) => {
    setSelectedEditionId(editionId);
  };

  const editionsLoaded = editions !== undefined;
  const noEditionsAvailable =
    editionsLoaded && (
      editions.length === 0 ||
      (editions.every((ed) => dayjs(ed.endTime) < dayjs()) && !editions.some((ed) => isEnrolledLoStatus(ed.status)))
    );
  const showWaitingEnrollment =
    editionsLoaded && !noEditionsAvailable && !selfEnrollAllowed && !hasUserEdition;

  const visibleEditions = (editions ?? [])
    .filter((edition) => {
      if (isUserEdition(edition.status)) return true;
      if (hasCompletedEdition) return false;
      if (hasPendingRequest) return edition.id === waitingApprovalEdition?.id;
      if (!selfEnrollAllowed || (enrolled && !editMode)) return false;
      return dayjs(edition.endTime) >= dayjs();
    })
    .sort((a, b) => a.startTime - b.startTime);

  const noOtherEnrollableEditions = editMode && enrolled &&
    !visibleEditions.some((edition) => edition.status !== "E");

  // Richiesta di cambio edizione in sospeso: l'utente è già iscritto (status "E") e c'è
  // un'altra edizione della stessa aula in attesa di approvazione (status "W").
  const hasPendingEditionChangeRequest = !!enrolledEdition && hasWaitingApprovalEdition;

  const hasOtherEnrollableEditions = (editions ?? []).some(
    (edition) =>
      edition.status !== "E" &&
      edition.status !== "C" &&
      edition.remainingPlaces > 0 &&
      dayjs(edition.endTime) >= dayjs()
  );

  // close edit mode when editions change
  useEffect(() => {
    setEditMode(false);
  }, [editions]);

  const editionsSubtitle = !selfEnrollAllowed || hasCompletedEdition
    ? ""
    : enrolled && !editMode ? descriptionEnrolled : description;

  return (
    <>
      <TitleAndSubtitle
        title={ title }
        subTitle={ editionsSubtitle }
        sxSubTitle={ {
          marginBottom:"0.75rem"
        } }
      />
      <EditionsContainer
        id="editions-tab-container"
        data-testid="editions-tab-container"
        aria-label={ t("editions_tab_aria_label") }
        noPadding={ noPadding }
      >
        { noEditionsAvailable &&
          <div style={ { marginBottom: "1rem" } } data-testid="editions-empty-state">
            <InfoBanner
              id="editions-empty"
              ariaLabel={ t("editions_empty_banner_aria_label") }
              backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
              textColor={ theme.customColors.textTag }
              borderColor={ theme.customColors.backgroundAccent }
              icon={ <InfoIcon size={ "1.5rem" } color={ theme.customColors.textTag } /> }
              title={ t("info_banner_title_editions") }
              label={ t("info_banner_description_editions") }
            />
          </div> }
        { showWaitingEnrollment && (
          <Stack
            data-testid="editions-waiting-enrollment"
            sx={ { marginBottom: "1rem" } }
          >
            <InfoBanner
              id="editions-waiting-enrollment"
              ariaLabel={ t("editions_waiting_enrollment_aria_label") }
              backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
              textColor={ theme.customColors.textTag }
              borderColor={ theme.customColors.backgroundAccent }
              icon={ <Hourglass size={ "1.25rem" } color={ theme.customColors.textTag } /> }
              label={ t("waiting_enrollment", "A breve verrai iscritto a un'edizione dell'aula") }
              sxContainer={ {
                padding: "0.5rem 1rem"
              } }
            />
          </Stack>
        ) }
        { isLoading || isPollingEditionChange || editionsIsFetching ? <SkeletonCard />
          : (
            <>
              { hasPendingEditionChangeRequest && (
                <div style={ { marginBottom: "1rem" } } data-testid="editions-pending-change-request">
                  <InfoBanner
                    id="editions-pending-change-request"
                    ariaLabel={ t("editions_pending_change_request_aria_label") }
                    backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
                    textColor={ theme.customColors.textTag }
                    borderColor={ theme.customColors.backgroundAccent }
                    icon={ <CircleAlert size={ "1.5rem" } color={ theme.customColors.backgroundAccent } /> }
                    title={ t("editions_pending_change_request_title") }
                    label={ t("editions_pending_change_request_label") }
                  />
                </div>
              ) }
              <RadioGroup
                id="editions-radio-group"
                data-testid="editions-radio-group"
                aria-label={ t("editions_radio_group_aria_label") }
                value={ selectedEditionId }
                onChange={ (e) => setSelectedEditionId(Number(e.target.value)) }
                sx={ {
                  maxHeight: "30rem",
                  overflowY: "auto",
                  flexDirection: "column",
                  flexWrap: "nowrap",
                  "::-webkit-scrollbar": {
                    width: "0.44rem",
                    backgroundColor: `${alpha(theme.customColors.exploreCardText, 0.2)}`,
                    borderRadius: "0.5rem"
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: `${alpha(theme.customColors.exploreCardText, 0.2)}!important`,
                    borderRadius: "0.5rem"
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: `${alpha(theme.customColors.exploreCardText, 1)}!important`,
                    borderRadius: "0.5rem"
                  }
                } }
              >
                { visibleEditions.map((edition) => (
                  <EditionCard
                    key={ edition.id }
                    learningObject={ learningObject }
                    edition={ edition }
                    editMode={ editMode }
                    isVirtual={ !!isVirtual }
                    isExpanded={ expandedEditionId === edition.id }
                    onToggleAccordion={ () => toggleAccordion(edition.id) }
                    onExpand={ () => expandEdition(edition.id) }
                    onSelect={ () => selectEdition(edition.id) }
                    oneEditionIsEnrolled={ enrolled || hasCompletedEdition }
                    theme={ theme }
                    t={ t }
                  />
                )) }

              </RadioGroup>
              { noOtherEnrollableEditions && (
                <div style={ { marginBottom: "1rem" } } data-testid="editions-no-other-editions">
                  <InfoBanner
                    id="editions-no-other-editions"
                    ariaLabel={ t("no_enrollable_editions") }
                    backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
                    textColor={ theme.customColors.textTag }
                    borderColor={ theme.customColors.backgroundAccent }
                    icon={ <InfoIcon size={ "1.5rem" } color={ theme.customColors.backgroundAccent } /> }
                    label={ t("no_enrollable_editions") }
                  />
                </div>
              ) }
              { enrolled && selfEnrollAllowed && (
                <Stack direction="row" gap={ 1 } alignItems="center">
                  { !editMode ? (
                    <>
                      <Tooltip
                        placement={ isMobile ? "bottom" : "right" }
                        title={ !hasOtherEnrollableEditions ? t("no_enrollable_editions") : "" }
                      >
                        <span style={ { display: "inline-flex" } }>
                          <ButtonDigited
                            id="editions-modify-button"
                            data-testid="editions-modify-button"
                            aria-label={ t("editions_modify_button_aria_label") }
                            typology="secondary"
                            disabled={ !hasOtherEnrollableEditions }
                            onClick={ handleEnterEditMode }
                          >
                            { t("change_edition") }
                          </ButtonDigited>
                        </span>
                      </Tooltip>
                      { /* La disiscrizione compare in entrambi gli stati: con una sola edizione
                           "Modifica edizione" è disabilitato e la sezione non si apre mai, quindi il
                           solo pulsante interno la renderebbe irraggiungibile. */ }
                      { enrolledEdition && (
                        <ButtonDigited
                          id="editions-unsubscribe-button"
                          data-testid="editions-unsubscribe-button"
                          aria-label={ t("unsubscribe") }
                          typology="tertiary"
                          disabled={ isUnsubscribing || isPollingEditionChange || editionsIsFetching }
                          onClick={ () => setShowUnsubscribeConfirm(true) }
                        >
                          { isUnsubscribing ? t("unsubscribing") : t("unsubscribe") }
                        </ButtonDigited>
                      ) }
                    </>
                  ) : (
                    <>
                      <ButtonDigited
                        id="editions-save-button"
                        data-testid="editions-save-button"
                        aria-label={ t("editions_save_button_aria_label") }
                        typology="secondary"
                        disabled={ isChangingEdition || isPollingEditionChange || editionsIsFetching }
                        onClick={ handleEnroll }
                      >
                        { t("editions_save_button") }
                      </ButtonDigited>
                      { enrolledEdition && (
                        <ButtonDigited
                          id="editions-unsubscribe-edit-button"
                          data-testid="editions-unsubscribe-edit-button"
                          aria-label={ t("unsubscribe") }
                          typology="tertiary"
                          disabled={ isUnsubscribing || isPollingEditionChange || editionsIsFetching }
                          onClick={ () => setShowUnsubscribeConfirm(true) }
                        >
                          { isUnsubscribing ? t("unsubscribing") : t("unsubscribe") }
                        </ButtonDigited>
                      ) }
                      <ButtonDigited
                        id="editions-cancel-edit-button"
                        data-testid="editions-cancel-edit-button"
                        aria-label={ t("annul") }
                        typology="tertiary"
                        disabled={ isPollingEditionChange || editionsIsFetching }
                        onClick={ handleCancelEdit }
                      >
                        { t("annul") }
                      </ButtonDigited>
                    </>
                  ) }
                </Stack>
              ) }

              { /* utente non enrolled, puo' iscriversi (o richiedere l'iscrizione, se l'aula la
                   subordina all'approvazione del responsabile). Se l'attivita' o una sua edizione
                   e' gia' completata ("C") il bottone non deve piu' comparire.
                   //TODO verificare con UX/UI per quanto riguarda la visibilita' del bottone */ }
              { !enrolled && selfEnrollAllowed && !editMode && !hasCompletedEdition && (
                <ButtonDigited
                  id="editions-enroll-button"
                  data-testid="editions-enroll-button"
                  aria-label={ hasPendingRequest
                    ? t("validation_text")
                    : t("editions_enroll_button_aria_label") }
                  typology="primary"
                  disabled={ hasPendingRequest
                    || selectedEditionId === null
                    || isLoading
                    || isEnrolling
                    || isPollingEditionChange }
                  onClick={ handleEnroll }
                >
                  { hasPendingRequest
                    ? t("validation_text")
                    : enrollmentNeedsApproval
                      ? t("register_request")
                      : t("enroll_edition") }
                </ButtonDigited>
              ) }
              { hasPendingRequest && !hidePendingApprovalBanner && (
                <Stack sx={ { marginTop: "1rem" } } data-testid="editions-pending-approval">
                  <InfoBanner
                    id="editions-pending-approval"
                    ariaLabel={ t("object_available_once_approved") }
                    backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
                    textColor={ theme.customColors.textTag }
                    borderColor={ theme.customColors.backgroundAccent }
                    icon={ <Hourglass size={ "1.25rem" } color={ theme.customColors.textTag } /> }
                    label={ t("object_available_once_approved") }
                    sxContainer={ { padding: "0.5rem 1rem" } }
                  />
                </Stack>
              ) }
            </>
          )
        }
      </EditionsContainer>
      { enrolledEdition && (
        <UnsubscribeEditionConfirmModal
          editionId={ enrolledEdition.id }
          objectId={ learningObject.id }
          open={ showUnsubscribeConfirm }
          onClose={ () => setShowUnsubscribeConfirm(false) }
          onSuccess={ () => {
            invalidateLearningObject?.();
            refetchEditions?.();
            invalidateDescendantsClassrooms?.();
          } }
          onUnsubscribingChange={ setIsUnsubscribing }
          title={ t("unsubscribe_edition_confirm_title") }
          description={ isEnrolledEditionInProgress ? t("edition_in_progress_warning") : "" }
          showWarningIcon={ isEnrolledEditionInProgress }
          closeOnSuccess
        />
      ) }
      <EditionInProgressWarningModal
        open={ showChangeEditionWarning }
        onClose={ () => setShowChangeEditionWarning(false) }
        onConfirm={ confirmChangeEdition }
      />
    </>
  );
};
